import { useMemo, useState } from 'react';
import { SelectBillingCountryModalWrapper } from './style';
import CloseMIcon from 'crono-fe-common/icons/Icon-Close';
import { colors } from 'crono-fe-common/theme';
import { CircularProgress, Typography } from '@mui/material';
import BillingCountryPicker from 'pages/signup/planSteps/licenseAmount/billingCountryPicker';
import {
  CancelButton,
  MainButton,
} from 'crono-fe-common/components/CronoButton';
import useGetSubscriptionProducts from 'hooks/services/subscription/useGetSubscriptionProducts';
import SubscriptionPlanType from 'crono-fe-common/types/enums/subscriptionPlanType';
import { BlurBackgroundDivFixed } from 'crono-fe-common/components/Layout/BlurBackgroundDiv';
import { getStripeEnvironmentToUse } from 'utils/businessLogic';
import { StripeEnvironment } from 'crono-fe-common/types/otherSettings';
import { BillingPlan } from 'pages/signup/model';

interface IProps {
  close: () => void;
  onSelect: (priceId: string, stripeEnvironment: StripeEnvironment) => void;
  plan: SubscriptionPlanType;
  billingPlan: BillingPlan;
  isLoading: boolean;
}

const SelectBillingCountryModal = ({
  close,
  onSelect,
  plan,
  billingPlan,
  isLoading,
}: IProps) => {
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const stripeEnvironmentToUse = useMemo(() => {
    return getStripeEnvironmentToUse(selectedCountry);
  }, [selectedCountry]);

  const {
    data: subscriptionProducts,
    isLoading: isLoadingSubscriptionProducts,
  } = useGetSubscriptionProducts(stripeEnvironmentToUse);

  const handleCheckout = () => {
    if (!selectedCountry) return;

    const selectedProduct = subscriptionProducts?.data.data.find((product) => {
      return product.subscriptionType === plan;
    });
    if (!selectedProduct) return;
    const selectedPrice = selectedProduct.prices.find((price) => {
      return price.paymentCycle === billingPlan;
    });
    if (!selectedPrice) return;
    onSelect(selectedPrice.id || '', stripeEnvironmentToUse || 'Uk');
  };

  return (
    <BlurBackgroundDivFixed>
      <SelectBillingCountryModalWrapper>
        <div className="close-button" onClick={close}>
          <CloseMIcon color={colors.grey11} />
        </div>
        <div className="top-part">
          <div className="text-container">
            <Typography
              fontSize={24}
              fontWeight={700}
              lineHeight={'30px'}
              color={colors.mainDark}
            >
              Select your billing country:
            </Typography>
            <Typography
              fontSize={14}
              lineHeight={'24px'}
              fontWeight={500}
              color={colors.grey11}
            >
              Once selected the billing country will be permanent and used for
              tax calculation purposes. Make sure to select the correct billing
              country of your organization.
            </Typography>
          </div>
          <div className="select-container">
            <BillingCountryPicker
              value={selectedCountry}
              onChange={(country) => setSelectedCountry(country)}
            />
          </div>
        </div>
        <div className="buttons-wrapper">
          <CancelButton
            onClick={() => {
              close();
            }}
            style={{
              margin: 0,
              height: 48,
              width: 132,
            }}
          >
            Cancel
          </CancelButton>
          {isLoading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 132,
                height: 48,
              }}
            >
              <CircularProgress size={48} />
            </div>
          ) : (
            <MainButton
              onClick={handleCheckout}
              disabled={!selectedCountry || isLoadingSubscriptionProducts}
              style={{
                margin: 0,
                height: 48,
                width: 132,
              }}
            >
              Checkout
            </MainButton>
          )}
        </div>
      </SelectBillingCountryModalWrapper>
    </BlurBackgroundDivFixed>
  );
};

export default SelectBillingCountryModal;
